/**
 * A single spreadsheet column: a sparse map of 65,536-row chunks, one string
 * dictionary, and a tracked column type.
 *
 * The chunk map is sparse, so the 5,000,000 addressable rows of Requirement 1.2
 * cost nothing until they are written, and clearing the last value in a chunk
 * releases it again (unless it still carries formatting).
 *
 * Type tracking keeps a per-kind non-empty count, from which the column type is
 * derived after every write: no non-empty cells is `'empty'`, one kind present
 * is that kind, and two or more kinds present is `'mixed'` — the demotion the
 * import type inference of Requirement 10.7 relies on. Error cells are formula
 * outcomes rather than a column data type, so they are excluded from the
 * derivation. A declared type (`'date'`, or a type asserted by the importer)
 * survives while the stored kinds remain consistent with it, and demotes to
 * `'mixed'` when they stop being consistent.
 */

import { CHUNK_ROWS, EMPTY, MAX_ROWS, type CellValue, type ScalarType } from '../types.js';
import {
  chunkIndexOfRow,
  chunkMemory,
  clearFormula,
  createChunk,
  DEFAULT_STYLE_ID,
  errorFromOrdinal,
  firstRowOfChunk,
  getErrorDetail,
  getFormula,
  getKind,
  getPayload,
  getStyleId,
  isChunkDisposable,
  KIND_BOOL,
  KIND_COUNT,
  KIND_EMPTY,
  KIND_ERROR,
  KIND_NUMBER,
  KIND_TEXT,
  localRowOf,
  setBool,
  setEmpty,
  setError,
  setFormula,
  setNumber,
  setStyleId,
  setTextCode,
  type CellKind,
  type Chunk,
  type ErrorDetail,
  type FormulaCell,
} from './chunk.js';
import {
  createDictionary,
  dictionaryBytes,
  intern,
  stringOf,
  type StringDictionary,
} from './dictionary.js';

export interface Column {
  /** Tracked column type; `'mixed'` once two kinds coexist (Requirement 10.7). */
  type: ScalarType;
  /** Allocated on the first text write. */
  dict?: StringDictionary;
  /** Sparse: an absent chunk means every row in its span is empty. */
  chunks: Map<number, Chunk>;
  /** Non-empty cell count per {@link CellKind}. Internal bookkeeping. */
  kindCounts: number[];
  /** Type asserted by the importer, honoured while the stored kinds agree. */
  declaredType?: ScalarType;
}

/** Per-column memory accounting (Requirement 14.6). */
export interface ColumnMemory {
  chunkCount: number;
  /** Rows made addressable by allocated chunks. */
  allocatedCells: number;
  /** Typed-array bytes; at most `MAX_BYTES_PER_CELL * allocatedCells`. */
  cellBytes: number;
  /** Fixed per-chunk bookkeeping. */
  overheadBytes: number;
  /** Sparse formula and error-metadata bytes. */
  sparseBytes: number;
  /** Retained string-dictionary bytes. */
  dictBytes: number;
  /** Sum of the four byte figures above. */
  totalBytes: number;
}

export function createColumn(): Column {
  return { type: 'empty', chunks: new Map(), kindCounts: new Array<number>(KIND_COUNT).fill(0) };
}

function checkRow(row: number): void {
  if (!Number.isInteger(row) || row < 0 || row >= MAX_ROWS) {
    throw new RangeError(`row out of range: ${row}`);
  }
}

/** The chunk holding `row`, or `undefined` when that span is unallocated. */
export function chunkOfRow(col: Column, row: number): Chunk | undefined {
  return col.chunks.get(chunkIndexOfRow(row));
}

/** The chunk holding `row`, allocating it when absent. */
export function ensureChunkOfRow(col: Column, row: number): Chunk {
  const index = chunkIndexOfRow(row);
  const existing = col.chunks.get(index);
  if (existing !== undefined) return existing;
  const chunk = createChunk();
  col.chunks.set(index, chunk);
  return chunk;
}

/** Drops the chunk holding `row` when it no longer carries any information. */
function pruneChunkOfRow(col: Column, row: number): void {
  const index = chunkIndexOfRow(row);
  const chunk = col.chunks.get(index);
  if (chunk !== undefined && isChunkDisposable(chunk)) col.chunks.delete(index);
}

function ensureDict(col: Column): StringDictionary {
  const existing = col.dict;
  if (existing !== undefined) return existing;
  const dict = createDictionary();
  col.dict = dict;
  return dict;
}

export function getValue(col: Column, row: number): CellValue {
  checkRow(row);
  const chunk = chunkOfRow(col, row);
  if (chunk === undefined) return EMPTY;
  const local = localRowOf(row);
  const kind = getKind(chunk, local);
  switch (kind) {
    case KIND_EMPTY:
      return EMPTY;
    case KIND_NUMBER:
      return { k: 'number', v: getPayload(chunk, local) };
    case KIND_TEXT: {
      const dict = col.dict;
      if (dict === undefined) return EMPTY;
      return { k: 'text', v: stringOf(dict, getPayload(chunk, local)) };
    }
    case KIND_BOOL:
      return { k: 'bool', v: getPayload(chunk, local) !== 0 };
    case KIND_ERROR: {
      const value: CellValue = { k: 'error', v: errorFromOrdinal(getPayload(chunk, local)) };
      const detail = getErrorDetail(chunk, local);
      if (detail === undefined) return value;
      return {
        ...value,
        ...(detail.detail === undefined ? {} : { detail: detail.detail }),
        ...(detail.origin === undefined ? {} : { origin: detail.origin }),
      };
    }
  }
}

export function setValue(col: Column, row: number, value: CellValue): void {
  checkRow(row);
  if (value.k === 'empty') {
    clearValue(col, row);
    return;
  }
  const chunk = ensureChunkOfRow(col, row);
  const local = localRowOf(row);
  const previous = getKind(chunk, local);

  switch (value.k) {
    case 'number':
      setNumber(chunk, local, value.v);
      countKind(col, previous, KIND_NUMBER);
      break;
    case 'text':
      setTextCode(chunk, local, intern(ensureDict(col), value.v));
      countKind(col, previous, KIND_TEXT);
      break;
    case 'bool':
      setBool(chunk, local, value.v);
      countKind(col, previous, KIND_BOOL);
      break;
    case 'error': {
      const detail: ErrorDetail = {
        ...(value.detail === undefined ? {} : { detail: value.detail }),
        ...(value.origin === undefined ? {} : { origin: value.origin }),
      };
      setError(chunk, local, value.v, detail);
      countKind(col, previous, KIND_ERROR);
      break;
    }
  }
}

/**
 * Clears the value at `row` while keeping its style and, when the chunk holds
 * nothing else, releasing the chunk (Requirement 2.8).
 */
export function clearValue(col: Column, row: number): void {
  checkRow(row);
  const chunk = chunkOfRow(col, row);
  if (chunk === undefined) return;
  const local = localRowOf(row);
  const previous = getKind(chunk, local);
  setEmpty(chunk, local);
  clearFormula(chunk, local);
  countKind(col, previous, KIND_EMPTY);
  pruneChunkOfRow(col, row);
}

export function getCellStyleId(col: Column, row: number): number {
  checkRow(row);
  const chunk = chunkOfRow(col, row);
  return chunk === undefined ? 0 : getStyleId(chunk, localRowOf(row));
}

export function setCellStyleId(col: Column, row: number, styleId: number): void {
  checkRow(row);
  if (styleId === 0 && chunkOfRow(col, row) === undefined) return;
  setStyleId(ensureChunkOfRow(col, row), localRowOf(row), styleId);
}

export function getFormulaAt(col: Column, row: number): FormulaCell | undefined {
  checkRow(row);
  const chunk = chunkOfRow(col, row);
  return chunk === undefined ? undefined : getFormula(chunk, localRowOf(row));
}

/**
 * Stores a formula and its cached value. The cached value is written through to
 * the columnar payload, so readers and scans never special-case formula cells.
 */
export function setFormulaAt(col: Column, row: number, cell: FormulaCell): void {
  checkRow(row);
  setValue(col, row, cell.cached);
  setFormula(ensureChunkOfRow(col, row), localRowOf(row), cell);
}

export function clearFormulaAt(col: Column, row: number): void {
  checkRow(row);
  const chunk = chunkOfRow(col, row);
  if (chunk === undefined) return;
  clearFormula(chunk, localRowOf(row));
  pruneChunkOfRow(col, row);
}

/** Asserts an inferred type (import path, Requirement 10.7). */
export function declareColumnType(col: Column, type: ScalarType): void {
  col.declaredType = type;
  col.type = deriveType(col);
}

/** True when no chunk is allocated for this column. */
export function isColumnEmpty(col: Column): boolean {
  return col.chunks.size === 0;
}

/** Non-empty cell count across the column. */
export function columnCellCount(col: Column): number {
  let total = 0;
  for (let kind = 1; kind < KIND_COUNT; kind++) total += col.kindCounts[kind] ?? 0;
  return total;
}

export function columnMemory(col: Column): ColumnMemory {
  let allocatedCells = 0;
  let cellBytes = 0;
  let overheadBytes = 0;
  let sparseBytes = 0;
  for (const chunk of col.chunks.values()) {
    const m = chunkMemory(chunk);
    allocatedCells += m.allocatedCells;
    cellBytes += m.cellBytes;
    overheadBytes += m.overheadBytes;
    sparseBytes += m.sparseBytes;
  }
  const dictBytes = col.dict === undefined ? 0 : dictionaryBytes(col.dict);
  return {
    chunkCount: col.chunks.size,
    allocatedCells,
    cellBytes,
    overheadBytes,
    sparseBytes,
    dictBytes,
    totalBytes: cellBytes + overheadBytes + sparseBytes + dictBytes,
  };
}

function countKind(col: Column, from: CellKind, to: CellKind): void {
  if (from !== to) {
    if (from !== KIND_EMPTY) col.kindCounts[from] = (col.kindCounts[from] ?? 1) - 1;
    if (to !== KIND_EMPTY) col.kindCounts[to] = (col.kindCounts[to] ?? 0) + 1;
  }
  col.type = deriveType(col);
}

/** Types present in the stored data, ignoring error cells. */
function storedTypes(col: Column): ScalarType[] {
  const present: ScalarType[] = [];
  if ((col.kindCounts[KIND_NUMBER] ?? 0) > 0) present.push('number');
  if ((col.kindCounts[KIND_TEXT] ?? 0) > 0) present.push('text');
  if ((col.kindCounts[KIND_BOOL] ?? 0) > 0) present.push('bool');
  return present;
}

function deriveType(col: Column): ScalarType {
  const present = storedTypes(col);
  const stored: ScalarType = present.length === 0 ? 'empty' : present.length === 1 ? present[0] as ScalarType : 'mixed';
  const declared = col.declaredType;
  if (declared === undefined) return stored;
  if (stored === 'empty') return declared;
  // Dates are stored as numbers plus a number format, so a declared date column
  // stays a date column while its values remain numeric.
  if (declared === 'date') return stored === 'number' ? 'date' : 'mixed';
  return stored === declared ? declared : 'mixed';
}

/* ------------------------------------------------------- structural edits */

/**
 * Everything one row of a column carries. Structural edits move slots rather
 * than values, so a shifted row keeps its formatting and its formula source
 * alongside its value (Requirement 4.13).
 */
export interface RowSlot {
  value: CellValue;
  styleId: number;
  formula?: FormulaCell;
}

export function readRowSlot(col: Column, row: number): RowSlot {
  const formula = getFormulaAt(col, row);
  return {
    value: getValue(col, row),
    styleId: getCellStyleId(col, row),
    ...(formula === undefined ? {} : { formula }),
  };
}

export function writeRowSlot(col: Column, row: number, slot: RowSlot): void {
  setValue(col, row, slot.value);
  setCellStyleId(col, row, slot.styleId);
  if (slot.formula === undefined) clearFormulaAt(col, row);
  else setFormulaAt(col, row, slot.formula);
}

/** Empties one row completely: value, formula, and style. */
export function clearRowSlot(col: Column, row: number): void {
  if (chunkOfRow(col, row) === undefined) return;
  setCellStyleId(col, row, DEFAULT_STYLE_ID);
  clearFormulaAt(col, row);
  // Clearing the value last gives the chunk a chance to be released.
  clearValue(col, row);
}

/**
 * Local rows of `chunk` within `[fromLocal, toLocal]` that carry anything at
 * all: a value, a non-default style, or a formula.
 *
 * A chunk that is uniformly default-styled and formula-free carries nothing
 * beyond its values, so the scan stops as soon as it has seen every one of the
 * chunk's non-empty cells. That is the common case, and it keeps a shift on a
 * sparse sheet proportional to the data rather than to the 65,536-row span.
 */
function meaningfulLocalRows(chunk: Chunk, fromLocal: number, toLocal: number): number[] {
  const rows: number[] = [];
  const styleIds = chunk.styleIds;
  const uniformStyled = chunk.uniformStyleId !== DEFAULT_STYLE_ID;
  const formulas = chunk.formulas;
  const valuesOnly =
    styleIds === undefined && !uniformStyled && (formulas === undefined || formulas.size === 0);

  // Count the values that sit before the scan window, so the early exit below
  // can tell when the window has yielded every remaining one.
  let seen = 0;
  if (valuesOnly) {
    for (let local = 0; local < fromLocal; local++) {
      if ((chunk.kinds[local] ?? KIND_EMPTY) !== KIND_EMPTY) seen += 1;
    }
  }

  for (let local = fromLocal; local <= toLocal; local++) {
    const nonEmpty = (chunk.kinds[local] ?? KIND_EMPTY) !== KIND_EMPTY;
    if (nonEmpty) seen += 1;
    const styled =
      styleIds === undefined ? uniformStyled : (styleIds[local] ?? DEFAULT_STYLE_ID) !== DEFAULT_STYLE_ID;
    if (nonEmpty || styled || formulas?.has(local) === true) rows.push(local);
    if (valuesOnly && seen === chunk.nonEmpty) break;
  }
  return rows;
}

/** Allocated chunk indices at or after the chunk holding `from`, in scan order. */
function chunkKeysFrom(col: Column, from: number, descending: boolean): number[] {
  const first = chunkIndexOfRow(from);
  const keys: number[] = [];
  for (const key of col.chunks.keys()) {
    if (key >= first) keys.push(key);
  }
  return keys.sort((a, b) => (descending ? b - a : a - b));
}

/** Rows of `[r0, r1]` that carry anything, ascending. */
function meaningfulRowsIn(col: Column, r0: number, r1: number): number[] {
  const rows: number[] = [];
  const firstChunk = chunkIndexOfRow(r0);
  const lastChunk = chunkIndexOfRow(r1);
  for (let key = firstChunk; key <= lastChunk; key++) {
    const chunk = col.chunks.get(key);
    if (chunk === undefined) continue;
    const base = firstRowOfChunk(key);
    const fromLocal = key === firstChunk ? r0 - base : 0;
    const toLocal = key === lastChunk ? r1 - base : CHUNK_ROWS - 1;
    for (const local of meaningfulLocalRows(chunk, fromLocal, toLocal)) rows.push(base + local);
  }
  return rows;
}

/**
 * Shifts every row at or after `at` down by `count`, leaving `count` empty rows
 * behind.
 *
 * Rows are moved highest-first, so a destination is always vacated before it is
 * written and no scratch copy of the column is needed. Content that would land
 * past the last addressable row is dropped; the sheet layer rejects the edit
 * before that can lose a non-empty cell.
 */
export function insertColumnRows(col: Column, at: number, count: number): void {
  for (const key of chunkKeysFrom(col, at, true)) {
    const chunk = col.chunks.get(key);
    if (chunk === undefined) continue;
    const base = firstRowOfChunk(key);
    const fromLocal = key === chunkIndexOfRow(at) ? at - base : 0;
    const locals = meaningfulLocalRows(chunk, fromLocal, CHUNK_ROWS - 1);
    for (let i = locals.length - 1; i >= 0; i--) {
      const row = base + (locals[i] as number);
      const target = row + count;
      if (target < MAX_ROWS) writeRowSlot(col, target, readRowSlot(col, row));
      clearRowSlot(col, row);
    }
  }
}

/**
 * Removes the `count` rows starting at `at` and pulls every row below them up.
 *
 * The removed band is cleared first, then rows are moved lowest-first, so a
 * destination is always vacated before it is written.
 */
export function deleteColumnRows(col: Column, at: number, count: number): void {
  for (const row of meaningfulRowsIn(col, at, Math.min(at + count - 1, MAX_ROWS - 1))) {
    clearRowSlot(col, row);
  }
  const from = at + count;
  if (from >= MAX_ROWS) return;
  for (const key of chunkKeysFrom(col, from, false)) {
    const chunk = col.chunks.get(key);
    if (chunk === undefined) continue;
    const base = firstRowOfChunk(key);
    const fromLocal = key === chunkIndexOfRow(from) ? from - base : 0;
    for (const local of meaningfulLocalRows(chunk, fromLocal, CHUNK_ROWS - 1)) {
      const row = base + local;
      writeRowSlot(col, row - count, readRowSlot(col, row));
      clearRowSlot(col, row);
    }
  }
}

/* ------------------------------------------------------------- direct columnar bulk operations */

/**
 * Bulk writes an array of numbers directly into columnar chunks with zero per-cell object allocations.
 */
export function setColumnNumbers(col: Column, startRow: number, values: ArrayLike<number | null | undefined>): void {
  const n = values.length;
  for (let i = 0; i < n; i++) {
    const row = startRow + i;
    if (row >= MAX_ROWS) break;
    const val = values[i];
    if (val === null || val === undefined || Number.isNaN(val)) {
      clearValue(col, row);
      continue;
    }
    const chunk = ensureChunkOfRow(col, row);
    const local = localRowOf(row);
    const prev = getKind(chunk, local);
    setNumber(chunk, local, val);
    countKind(col, prev, KIND_NUMBER);
  }
  col.declaredType = 'number';
}

/**
 * Bulk writes an array of booleans directly into columnar chunks.
 */
export function setColumnBools(col: Column, startRow: number, values: ArrayLike<boolean | null | undefined>): void {
  const n = values.length;
  for (let i = 0; i < n; i++) {
    const row = startRow + i;
    if (row >= MAX_ROWS) break;
    const val = values[i];
    if (val === null || val === undefined) {
      clearValue(col, row);
      continue;
    }
    const chunk = ensureChunkOfRow(col, row);
    const local = localRowOf(row);
    const prev = getKind(chunk, local);
    setBool(chunk, local, Boolean(val));
    countKind(col, prev, KIND_BOOL);
  }
  col.declaredType = 'bool';
}

/**
 * Bulk writes an array of strings directly into columnar chunks with dictionary interning.
 */
export function setColumnTexts(col: Column, startRow: number, values: ArrayLike<string | null | undefined>): void {
  const n = values.length;
  const dict = ensureDict(col);
  for (let i = 0; i < n; i++) {
    const row = startRow + i;
    if (row >= MAX_ROWS) break;
    const val = values[i];
    if (val === null || val === undefined || val === '') {
      clearValue(col, row);
      continue;
    }
    const chunk = ensureChunkOfRow(col, row);
    const local = localRowOf(row);
    const prev = getKind(chunk, local);
    setTextCode(chunk, local, intern(dict, String(val)));
    countKind(col, prev, KIND_TEXT);
  }
  col.declaredType = 'text';
}

/**
 * Bulk writes a heterogeneous or auto-typed columnar vector directly into chunks.
 */
export function setColumnVector(col: Column, startRow: number, values: ArrayLike<number | boolean | string | null | undefined>): void {
  const n = values.length;
  const dict = ensureDict(col);
  for (let i = 0; i < n; i++) {
    const row = startRow + i;
    if (row >= MAX_ROWS) break;
    const val = values[i];
    if (val === null || val === undefined || val === '') {
      clearValue(col, row);
      continue;
    }
    const chunk = ensureChunkOfRow(col, row);
    const local = localRowOf(row);
    const prev = getKind(chunk, local);

    if (typeof val === 'number' && !Number.isNaN(val)) {
      setNumber(chunk, local, val);
      countKind(col, prev, KIND_NUMBER);
    } else if (typeof val === 'boolean') {
      setBool(chunk, local, val);
      countKind(col, prev, KIND_BOOL);
    } else {
      setTextCode(chunk, local, intern(dict, String(val)));
      countKind(col, prev, KIND_TEXT);
    }
  }
}

/**
 * Exports a range of column rows as an Arrow/DuckDB-compatible columnar vector with a null bitmap.
 */
export interface ArrowColumnVector {
  type: ScalarType;
  length: number;
  nullCount: number;
  nullBitmap: Uint8Array;
  numbers?: Float64Array;
  booleans?: Uint8Array;
  strings?: string[];
  mixed?: (number | boolean | string | null)[];
}

export function exportArrowVector(col: Column, startRow: number, count: number): ArrowColumnVector {
  const bitmapBytes = Math.ceil(count / 8);
  const nullBitmap = new Uint8Array(bitmapBytes).fill(0xff); // 1 = valid, 0 = null
  let nullCount = 0;
  const colType = col.type;

  const setBitNull = (idx: number): void => {
    const byteIdx = idx >> 3;
    const bitIdx = idx & 7;
    const curr = nullBitmap[byteIdx];
    if (curr !== undefined) {
      nullBitmap[byteIdx] = curr & ~(1 << bitIdx);
    }
    nullCount++;
  };

  if (colType === 'number') {
    const numbers = new Float64Array(count);
    for (let i = 0; i < count; i++) {
      const row = startRow + i;
      const v = getValue(col, row);
      if (v.k === 'number') {
        numbers[i] = v.v;
      } else {
        numbers[i] = 0;
        setBitNull(i);
      }
    }
    return { type: 'number', length: count, nullCount, nullBitmap, numbers };
  }

  if (colType === 'bool') {
    const booleans = new Uint8Array(count);
    for (let i = 0; i < count; i++) {
      const row = startRow + i;
      const v = getValue(col, row);
      if (v.k === 'bool') {
        booleans[i] = v.v ? 1 : 0;
      } else {
        booleans[i] = 0;
        setBitNull(i);
      }
    }
    return { type: 'bool', length: count, nullCount, nullBitmap, booleans };
  }

  if (colType === 'text') {
    const strings: string[] = new Array(count);
    for (let i = 0; i < count; i++) {
      const row = startRow + i;
      const v = getValue(col, row);
      if (v.k === 'text') {
        strings[i] = v.v;
      } else {
        strings[i] = '';
        setBitNull(i);
      }
    }
    return { type: 'text', length: count, nullCount, nullBitmap, strings };
  }

  // Mixed or empty
  const mixed: (number | boolean | string | null)[] = new Array(count);
  for (let i = 0; i < count; i++) {
    const row = startRow + i;
    const v = getValue(col, row);
    if (v.k === 'empty') {
      mixed[i] = null;
      setBitNull(i);
    } else if (v.k === 'error') {
      mixed[i] = `#${v.v}!`;
      setBitNull(i);
    } else {
      mixed[i] = v.v;
    }
  }
  return { type: colType, length: count, nullCount, nullBitmap, mixed };
}

